import "@babylonjs/loaders/glTF";
import { Space, Vector3 } from "@babylonjs/core";
import { createScene } from "./scene";
import { sfx } from "./sfx";
import { dispatchDeliveryVan, dispatchAmbientVan } from "./world/vehicles";
import { spawnConfetti, spawnSparks } from "./world/effects";
import { createGhostManager } from "./world/ghosts";
import { checkAchievements, loadStats, saveStats, type AchievementContext } from "./achievements";
import { connectLive } from "./net/live";
import { fetchMarket } from "./net/market";
import { fetchContracts, acceptContract } from "./net/contracts";
import { fetchPlayerWarehouses } from "./net/warehouse";
import { fetchPlayer, buy, repairPrinter, sellPrinter, upgradeWarehouse } from "./net/player";
import { fetchWorkers, hireWorker, fireWorker } from "./net/workers";
import { fetchReputation } from "./net/reputation";
import {
	UTAH_REGIONS,
	REP_THRESHOLDS,
	type MarketPart,
	type Contract,
	type Printer,
	type Player,
	type Worker,
	type WorkerRole,
	type ClientReputation,
} from "@utah-vr/shared";

const region = UTAH_REGIONS[0]!;
document.getElementById("region")!.textContent = `Region: ${region.displayName}`;

function currentSeasonInfo(): { name: string; icon: string; hint: string } {
	const m = new Date().getUTCMonth();
	if (m === 11 || m === 0 || m === 1) return { name: "Winter", icon: "❄️", hint: "Ski/resort contracts ×3" };
	if (m >= 2 && m <= 4) return { name: "Spring", icon: "🌱", hint: "Campus/research contracts ×2" };
	if (m >= 5 && m <= 7) return { name: "Summer", icon: "☀️", hint: "Drone/aerospace contracts ×2.5" };
	return { name: "Fall", icon: "🍂", hint: "Pharma/data contracts ×1.6" };
}
function refreshSeason() {
	const s = currentSeasonInfo();
	const el = document.getElementById("season");
	if (el) el.textContent = `${s.icon} ${s.name} — ${s.hint}`;
}
refreshSeason();
// Refresh every 10 minutes so a long session crosses a season boundary cleanly.
setInterval(refreshSeason, 10 * 60 * 1000);

const canvas = document.getElementById("app") as HTMLCanvasElement;
const { engine, scene, warehouse, cam, interactZones, npcs, weather, workerVisuals, enterXR } = await createScene(canvas, region);

await enterXR().catch(() => {
	document.getElementById("xr-status")!.textContent = "XR: flat mode";
});

const fmtPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;
const fmtDeadline = (iso: string) => {
	const hrs = Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 3.6e6));
	return hrs < 24 ? `${hrs}h` : `${Math.round(hrs / 24)}d`;
};

const marketList = document.getElementById("market-list")!;
const contractsList = document.getElementById("contracts-list")!;
const printerList = document.getElementById("printer-list")!;
const inventoryList = document.getElementById("inventory-list")!;
const walletEl = document.getElementById("wallet")!;
const toastEl = document.getElementById("toast")!;
const priceRowBySku = new Map<string, HTMLElement>();
const sparkBySku = new Map<string, SVGPathElement>();
const buyRowBySku = new Map<string, { btn: HTMLButtonElement; price: HTMLElement }>();

let market: MarketPart[] = [];
let player: Player | null = null;
const priceHistory = new Map<string, number[]>();
const HISTORY_LEN = 24;
function pushHistory(sku: string, cents: number) {
	let arr = priceHistory.get(sku);
	if (!arr) { arr = []; priceHistory.set(sku, arr); }
	arr.push(cents);
	while (arr.length > HISTORY_LEN) arr.shift();
}
function sparklinePath(values: number[], w = 60, h = 18): string {
	if (values.length < 2) return "";
	const min = Math.min(...values);
	const max = Math.max(...values);
	const range = max - min || 1;
	return values
		.map((v, i) => {
			const x = (i / (values.length - 1)) * w;
			const y = h - ((v - min) / range) * h;
			return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
		})
		.join(" ");
}
const contractById = new Map<string, Contract>();
const printerById = new Map<string, Printer>();
const stats = loadStats();
const workersList = document.getElementById("workers-list")!;
const statsList = document.getElementById("stats-list")!;
const reputationList = document.getElementById("reputation-list")!;
const workerById = new Map<string, Worker>();
const reputationByClient = new Map<string, ClientReputation>();

function renderReputation() {
	reputationList.innerHTML = "";
	const entries = Array.from(reputationByClient.values()).sort((a, b) => b.totalRevenue.cents - a.totalRevenue.cents);
	if (entries.length === 0) {
		reputationList.textContent = "No completed contracts yet.";
		return;
	}
	for (const r of entries) {
		const row = document.createElement("div");
		row.className = "row";
		row.style.flexDirection = "column";
		row.style.alignItems = "stretch";

		const head = document.createElement("div");
		head.style.cssText = "display:flex;justify-content:space-between;align-items:center;";
		const star = "★".repeat(r.level) + "☆".repeat(5 - r.level);
		head.innerHTML = `<span><span class="client">${r.displayName}</span> · <span style="color:#fd6;">${star}</span></span><span class="price">${fmtPrice(r.totalRevenue.cents)}</span>`;

		// Progress bar to next level
		const nextThreshold = REP_THRESHOLDS[r.level + 1] ?? REP_THRESHOLDS[r.level]!;
		const prevThreshold = REP_THRESHOLDS[r.level]!;
		const pct = Math.min(100, Math.max(0, 100 * (r.totalContracts - prevThreshold) / Math.max(1, nextThreshold - prevThreshold)));
		const bar = document.createElement("div");
		bar.style.cssText = "height:6px;background:rgba(255,255,255,0.1);border-radius:3px;margin-top:4px;overflow:hidden;";
		const fill = document.createElement("div");
		fill.style.cssText = `height:100%;width:${pct}%;background:linear-gradient(90deg,#fc7,#7df);transition:width 0.5s;`;
		bar.append(fill);

		const meta = document.createElement("div");
		meta.style.cssText = "font-size:11px;color:#bbb;margin-top:2px;";
		meta.textContent = `${r.totalContracts} contracts completed · payout +${(r.level * 10)}%`;

		row.append(head, bar, meta);
		reputationList.append(row);
	}
}

function renderWorkers() {
	workersList.innerHTML = "";
	if (workerById.size === 0) {
		workersList.textContent = "none hired";
		return;
	}
	let totalHourly = 0;
	for (const w of workerById.values()) {
		totalHourly += w.hourlyWage.cents;
		const row = document.createElement("div");
		row.className = "row";
		row.style.flexDirection = "column";
		row.style.alignItems = "stretch";
		const head = document.createElement("div");
		head.style.cssText = "display:flex;justify-content:space-between;align-items:center;";
		head.innerHTML = `<span><span class="client">${w.displayName}</span> · ${w.role}</span><span class="price">${fmtPrice(w.hourlyWage.cents)}/hr</span>`;
		const taskEl = document.createElement("div");
		taskEl.style.cssText = "font-size:11px;color:#fd6;";
		taskEl.textContent = `Status: ${w.currentTask ?? "idle"}`;
		const fireBtn = document.createElement("button");
		fireBtn.className = "sell";
		fireBtn.textContent = "Fire";
		fireBtn.style.marginTop = "4px";
		fireBtn.addEventListener("click", async () => {
			fireBtn.disabled = true;
			const res = await fireWorker(w.id);
			if (res.ok) {
				workerById.delete(w.id);
				renderWorkers();
				toast(`Fired ${w.displayName}`);
			} else {
				fireBtn.disabled = false;
				toast(`Fire failed: ${res.error ?? "?"}`, "#a22");
			}
		});
		row.append(head, taskEl, fireBtn);
		workersList.append(row);
	}
	// Show total wage burden
	const summary = document.createElement("div");
	summary.style.cssText = "margin-top:10px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.15);color:#fc7;font-weight:600;";
	summary.textContent = `Wages: ${fmtPrice(totalHourly)}/hr (${fmtPrice(totalHourly / 60)}/game-min)`;
	workersList.append(summary);
}

function renderStats() {
	const ach = stats;
	const lines: Array<[string, string]> = [
		["Contracts completed", `${ach.completedContracts}`],
		["Total revenue", fmtPrice(ach.totalPaidCents)],
		["Repairs performed", `${ach.repairCount}`],
		["Printers owned", `${printerById.size}`],
		["Workers hired", `${workerById.size}`],
		["Wallet", player ? fmtPrice(player.balance.cents) : "—"],
	];
	statsList.innerHTML = "";
	for (const [k, v] of lines) {
		const row = document.createElement("div");
		row.className = "row";
		const a = document.createElement("span"); a.className = "name"; a.textContent = k;
		const b = document.createElement("span"); b.className = "price"; b.textContent = v;
		row.append(a, b);
		statsList.append(row);
	}
}

document.querySelectorAll<HTMLButtonElement>('#worker-hire-row button[data-role]').forEach((btn) => {
	btn.addEventListener("click", async () => {
		btn.disabled = true;
		const role = btn.getAttribute("data-role") as WorkerRole;
		const res = await hireWorker(role);
		if (res.ok && res.worker) {
			workerById.set(res.worker.id, res.worker);
			renderWorkers();
			toast(`Hired ${res.worker.displayName} (${role})`);
			sfx.buy();
		} else {
			toast(`Hire failed: ${res.error ?? "?"}`, "#a22");
		}
		btn.disabled = false;
	});
});
const ghosts = createGhostManager(scene);
let mySessionId: string | null = null;
let liveSocket: WebSocket | null = null;

function runAchievementCheck() {
	let maxRepLevel = 0;
	for (const r of reputationByClient.values()) if (r.level > maxRepLevel) maxRepLevel = r.level;
	const ctx: AchievementContext = {
		balanceCents: player?.balance.cents ?? 0,
		completedContracts: stats.completedContracts,
		totalPaidCents: stats.totalPaidCents,
		repairCount: stats.repairCount,
		printersOwned: printerById.size,
		workersHired: workerById.size,
		racksOwned: player?.printerRacks ?? 0,
		maxClientRepLevel: maxRepLevel,
		uniqueClientsServed: reputationByClient.size,
	};
	checkAchievements(ctx, (a) => {
		toast(`🏆 ${a.title} — ${a.hint}`, "#a60");
		sfx.payout();
	});
}

function renderWallet() {
	if (!player) return;
	walletEl.textContent = fmtPrice(player.balance.cents);
}

function renderInventory() {
	if (!player) return;
	inventoryList.innerHTML = "";
	const entries = Object.entries(player.inventory).filter(([, q]) => q > 0);
	if (entries.length === 0) {
		inventoryList.textContent = "empty";
		return;
	}
	for (const [sku, qty] of entries) {
		const part = market.find((p) => p.sku === sku);
		const row = document.createElement("div");
		row.className = "row";
		const name = document.createElement("span");
		name.className = "name";
		name.textContent = part?.displayName ?? sku;
		const q = document.createElement("span");
		q.className = "price";
		q.textContent = `×${qty}`;
		row.append(name, q);
		inventoryList.append(row);
	}
}

let toastTimer: number | null = null;
function toast(msg: string, color = "#0a0") {
	toastEl.textContent = msg;
	toastEl.style.background = color;
	toastEl.style.display = "block";
	if (toastTimer) window.clearTimeout(toastTimer);
	toastTimer = window.setTimeout(() => (toastEl.style.display = "none"), 2500);
}

function renderMarket(parts: MarketPart[]) {
	market = parts;
	marketList.innerHTML = "";
	priceRowBySku.clear();
	buyRowBySku.clear();
	for (const p of parts) {
		const row = document.createElement("div");
		row.className = "row";
		row.style.flexDirection = "column";
		row.style.alignItems = "stretch";

		const top = document.createElement("div");
		top.style.display = "flex";
		top.style.justifyContent = "space-between";
		top.style.alignItems = "center";
		top.style.gap = "8px";

		const name = document.createElement("span");
		name.className = "name";
		name.title = p.displayName;
		name.textContent = p.displayName;
		const price = document.createElement("span");
		price.className = "price";
		price.textContent = fmtPrice(p.currentPrice.cents);

		const spark = document.createElementNS("http://www.w3.org/2000/svg", "svg");
		spark.setAttribute("width", "60");
		spark.setAttribute("height", "18");
		spark.setAttribute("class", "spark");
		spark.style.opacity = "0.9";
		const sparkPath = document.createElementNS("http://www.w3.org/2000/svg", "path");
		sparkPath.setAttribute("fill", "none");
		sparkPath.setAttribute("stroke", "#7df");
		sparkPath.setAttribute("stroke-width", "1.4");
		sparkPath.setAttribute("d", "");
		spark.appendChild(sparkPath);

		// Seed history with current price so we don't have a flat line.
		pushHistory(p.sku, p.currentPrice.cents);
		sparkPath.setAttribute("d", sparklinePath(priceHistory.get(p.sku)!));

		const buyBtn = document.createElement("button");
		buyBtn.className = "buy";
		buyBtn.textContent = "Buy";
		buyBtn.addEventListener("click", async () => {
			buyBtn.disabled = true;
			buyBtn.textContent = "…";
			const result = await buy(p.sku, 1);
			if (result.ok) {
				applyPlayer(result.player); // includes wallet + inventory + warehouse rack sync
				toast(`Bought ${p.displayName} — ${fmtPrice(result.spent.cents)}`);
				sfx.buy();
			} else {
				toast(`Buy failed: ${result.error}`, "#a22");
				sfx.error();
			}
			buyBtn.disabled = false;
			buyBtn.textContent = "Buy";
		});

		top.append(name, spark, price, buyBtn);
		row.append(top);
		marketList.append(row);
		priceRowBySku.set(p.sku, price);
		sparkBySku.set(p.sku, sparkPath);
		buyRowBySku.set(p.sku, { btn: buyBtn, price });
	}
	renderInventory();
}

function applyMarketUpdates(updates: { sku: string; cents: number }[]) {
	for (const u of updates) {
		const el = priceRowBySku.get(u.sku);
		if (!el) continue;
		el.textContent = fmtPrice(u.cents);
		el.classList.remove("flash");
		void el.offsetWidth;
		el.classList.add("flash");
		const part = market.find((p) => p.sku === u.sku);
		if (part) part.currentPrice = { currency: "USD", cents: u.cents };
		pushHistory(u.sku, u.cents);
		const sp = sparkBySku.get(u.sku);
		if (sp) sp.setAttribute("d", sparklinePath(priceHistory.get(u.sku)!));
	}
}

function renderContracts() {
	contractsList.innerHTML = "";
	const visible = Array.from(contractById.values()).filter(
		(c) => c.status === "open" || c.status === "in-progress",
	);
	if (visible.length === 0) {
		contractsList.textContent = "none";
		return;
	}
	for (const c of visible) {
		const row = document.createElement("div");
		row.className = "row";
		row.style.flexDirection = "column";
		row.style.alignItems = "stretch";

		const head = document.createElement("div");
		head.style.display = "flex";
		head.style.justifyContent = "space-between";
		head.style.alignItems = "center";
		head.innerHTML = `<span><span class="client">${c.clientName}</span> · <span class="payout">${fmtPrice(c.payout.cents)}</span></span>`;

		if (c.status === "open") {
			const btn = document.createElement("button");
			btn.className = "accept";
			btn.textContent = "Accept";
			btn.addEventListener("click", async () => {
				btn.disabled = true;
				btn.textContent = "…";
				try {
					const updated = await acceptContract(c.id);
					contractById.set(updated.id, updated);
					renderContracts();
					sfx.accept();
				} catch {
					btn.disabled = false;
					btn.textContent = "Retry";
				}
			});
			head.append(btn);
		} else {
			const tag = document.createElement("span");
			tag.style.cssText = "font-size:11px;color:#fd6;";
			tag.textContent = `${c.progress ?? 0}/${c.quantity}`;
			head.append(tag);
		}

		const desc = document.createElement("div");
		desc.style.cssText = "font-size:11px; color:#bbb;";
		desc.textContent = c.description;

		const meta = document.createElement("div");
		meta.className = "deadline";
		meta.textContent = `${c.quantity}× ${c.deliverableSku} · due ${fmtDeadline(c.deadline)}`;

		row.append(head, desc, meta);
		contractsList.append(row);
	}
}

function renderPrinters() {
	printerList.innerHTML = "";
	if (printerById.size === 0) {
		printerList.textContent = "none";
		return;
	}
	for (const p of printerById.values()) {
		const row = document.createElement("div");
		row.className = "row";
		row.style.flexDirection = "column";
		row.style.alignItems = "stretch";

		const top = document.createElement("div");
		top.style.display = "flex";
		top.style.justifyContent = "space-between";
		top.style.alignItems = "center";

		const name = document.createElement("span");
		name.className = "name";
		name.textContent = p.displayName;
		const status = document.createElement("span");
		status.className = `status-${p.status === "printing" ? "printing" : p.status === "failed" ? "failed" : "idle"}`;
		status.textContent = p.status === "failed" && p.failureMode ? `${p.status} (${p.failureMode})` : p.status;
		top.append(name, status);
		row.append(top);

		const actions = document.createElement("div");
		actions.style.cssText = "display:flex;gap:6px;margin-top:6px;flex-wrap:wrap;";

		if (p.status === "failed") {
			const btn = document.createElement("button");
			btn.className = "repair";
			btn.textContent = "Repair";
			btn.addEventListener("click", async () => {
				btn.disabled = true;
				btn.textContent = "…";
				const res = await repairPrinter(p.id);
				if (res.ok) { toast(`Repaired ${p.displayName}`); sfx.repair(); }
				else {
					// Sync local state with the server-returned current printer so
					// the UI immediately reflects "already repaired" cases.
					if (res.printer) {
						printerById.set(res.printer.id, res.printer);
						renderPrinters();
					}
					toast(`Repair failed: ${res.error ?? "?"}`, "#a22");
					sfx.error();
				}
				btn.disabled = false;
				btn.textContent = "Repair";
			});
			actions.append(btn);
		}

		if (p.status !== "printing") {
			const sellBtn = document.createElement("button");
			sellBtn.className = "sell";
			sellBtn.textContent = "Sell";
			sellBtn.addEventListener("click", async () => {
				if (!confirm(`Sell ${p.displayName} for ~40% refund?`)) return;
				sellBtn.disabled = true;
				const res = await sellPrinter(p.id);
				if (res.ok) {
					printerById.delete(p.id);
					warehouse.removePrinter(p.id);
					renderPrinters();
					toast(`Sold ${p.displayName} (+${fmtPrice(res.refundCents ?? 0)})`);
				} else {
					toast(`Sell failed: ${res.error ?? "?"}`, "#a22");
					sellBtn.disabled = false;
				}
			});
			actions.append(sellBtn);
		}

		if (actions.childElementCount > 0) row.append(actions);
		printerList.append(row);
	}

	// Append warehouse upgrade row at the bottom.
	const upgRow = document.createElement("div");
	upgRow.className = "row";
	upgRow.style.flexDirection = "column";
	upgRow.style.alignItems = "stretch";
	upgRow.style.marginTop = "10px";
	upgRow.style.paddingTop = "10px";
	upgRow.style.borderTop = "1px solid rgba(255,255,255,0.15)";
	upgRow.innerHTML = `<div style="font-weight:600;color:#7df;">Warehouse</div><div style="font-size:11px;color:#bbb;">Tap to expand capacity.</div>`;
	const upgBtn = document.createElement("button");
	upgBtn.className = "buy";
	upgBtn.textContent = "Upgrade warehouse";
	upgBtn.style.marginTop = "6px";
	upgBtn.addEventListener("click", async () => {
		upgBtn.disabled = true;
		const res = await upgradeWarehouse("wh-starter-1");
		if (res.ok) toast("Warehouse upgraded — more slots unlocked");
		else toast(`Upgrade failed: ${res.error ?? "?"}`, "#a22");
		upgBtn.disabled = false;
	});
	upgRow.append(upgBtn);
	printerList.append(upgRow);
}

function ensurePrinterMesh(p: Printer) {
	if (!warehouse.printers.has(p.id)) warehouse.addPrinter(p.id);
}

function syncMeshes() {
	for (const p of printerById.values()) ensurePrinterMesh(p);
	for (const id of Array.from(warehouse.printers.keys())) {
		if (!printerById.has(id)) warehouse.removePrinter(id);
	}
}

// Tap printer mesh in scene → if failed, attempt repair.
window.addEventListener("printer-tap", async (e: Event) => {
	const id = (e as CustomEvent<{ id: string }>).detail.id;
	const p = printerById.get(id);
	if (!p || p.status !== "failed") return;
	const res = await repairPrinter(id);
	if (res.ok) toast(`Repaired ${p.displayName}`);
	else toast(`Repair: ${res.error ?? "?"}`, "#a22");
});

function applyPlayer(p: Player) {
	player = p;
	renderWallet();
	renderInventory();
	warehouse.rack.setInventory(p.inventory);
	if (p.printerRacks) warehouse.setRackCount(p.printerRacks);
}

liveSocket = connectLive((msg) => {
	if (msg.type === "welcome") {
		applyPlayer(msg.player);
		mySessionId = msg.sessionId;
	} else if (msg.type === "presence") {
		if (msg.sessionId !== mySessionId) ghosts.upsert(msg.sessionId, msg.pose);
	} else if (msg.type === "presenceLeft") {
		ghosts.remove(msg.sessionId);
	} else if (msg.type === "marketTick") {
		applyMarketUpdates(msg.updates);
	} else if (msg.type === "contractUpdated") {
		contractById.set(msg.contract.id, msg.contract);
		renderContracts();
	} else if (msg.type === "printerUpdated") {
		const prev = printerById.get(msg.printer.id);
		printerById.set(msg.printer.id, msg.printer);
		ensurePrinterMesh(msg.printer);
		renderPrinters();
		if (msg.printer.status === "failed" && prev?.status !== "failed") {
			sfx.fail();
			const mesh = warehouse.printers.get(msg.printer.id);
			if (mesh) spawnSparks(scene, mesh.root);
		}
		if (prev?.status === "failed" && msg.printer.status === "idle") {
			stats.repairCount++;
			saveStats(stats);
			runAchievementCheck();
		}
	} else if (msg.type === "printerAdded") {
		printerById.set(msg.printer.id, msg.printer);
		warehouse.addPrinter(msg.printer.id);
		renderPrinters();
		toast(`New printer installed: ${msg.printer.displayName}`);
		sfx.buy();
		runAchievementCheck();
	} else if (msg.type === "playerUpdated") {
		applyPlayer(msg.player);
		renderStats();
		runAchievementCheck();
	} else if (msg.type === "workerHired") {
		workerById.set(msg.worker.id, msg.worker);
		workerVisuals.upsert(msg.worker);
		renderWorkers();
		renderStats();
	} else if (msg.type === "workerFired") {
		workerById.delete(msg.workerId);
		workerVisuals.remove(msg.workerId);
		renderWorkers();
		renderStats();
	} else if (msg.type === "workerUpdated") {
		workerById.set(msg.worker.id, msg.worker);
		workerVisuals.upsert(msg.worker);
		renderWorkers();
	} else if (msg.type === "reputationUpdated") {
		reputationByClient.set(msg.reputation.clientId, msg.reputation);
		renderReputation();
	} else if (msg.type === "dailyBonus") {
		const dayMsg = msg.streakDays === 1 ? "Welcome back!" : `${msg.streakDays}-day streak!`;
		toast(`🔥 Daily bonus +${fmtPrice(msg.cents)} — ${dayMsg}`, "#a60");
		sfx.payout();
	} else if (msg.type === "payout") {
		toast(`+${fmtPrice(msg.cents)} — ${msg.reason}`, "#063");
		sfx.payout();
		stats.completedContracts++;
		stats.totalPaidCents += msg.cents;
		saveStats(stats);
		// Dispatch a delivery van + confetti at the warehouse doorway.
		dispatchDeliveryVan(scene);
		spawnConfetti(scene, new Vector3(0, 1, 4));
		// Stack a packing-box on the warehouse table — visible fulfillment record.
		warehouse.packing.addBox();
		runAchievementCheck();
	}
});

await Promise.all([
	fetchMarket()
		.then((res) => renderMarket(res.parts))
		.catch((err) => (marketList.textContent = `error: ${err.message}`)),
	fetchContracts()
		.then((res) => {
			for (const c of res.contracts) contractById.set(c.id, c);
			renderContracts();
		})
		.catch((err) => (contractsList.textContent = `error: ${err.message}`)),
	fetchPlayerWarehouses()
		.then((res) => {
			for (const w of res.warehouses) for (const p of w.printers) printerById.set(p.id, p);
			syncMeshes();
			renderPrinters();
		})
		.catch((err) => (printerList.textContent = `error: ${err.message}`)),
	fetchPlayer()
		.then((p) => {
			applyPlayer(p);
		})
		.catch(() => {}),
	fetchWorkers()
		.then((res) => {
			for (const w of res.workers) {
				workerById.set(w.id, w);
				workerVisuals.upsert(w);
			}
			renderWorkers();
			renderStats();
		})
		.catch(() => {}),
	fetchReputation()
		.then((res) => {
			for (const r of res.reputations) reputationByClient.set(r.clientId, r);
			renderReputation();
		})
		.catch(() => {}),
]);

const ZONE_TO_TAB: Record<string, string> = {
	shop: "market-panel",
	kiosk: "contracts-panel",
};
let currentZoneId: string | null = null;
function setActivePanel(panelId: string | null) {
	for (const p of document.querySelectorAll<HTMLElement>(".panel")) p.classList.remove("open");
	for (const b of document.querySelectorAll<HTMLButtonElement>("#tabs button")) b.classList.remove("active");
	if (!panelId) return;
	const panel = document.getElementById(panelId);
	const btn = document.querySelector<HTMLButtonElement>(`#tabs button[data-target="${panelId}"]`);
	if (panel) panel.classList.add("open");
	if (btn) btn.classList.add("active");
}

let presenceTimer = 0;
let ambientTimer = 8;
let weatherTimer = 60; // first weather check at 1 minute
function rollWeather() {
	const r = Math.random();
	if (r < 0.7) weather.setKind("clear");
	else if (r < 0.9) weather.setKind("rain");
	else weather.setKind("snow");
}

// Kick off a few vans immediately so the world isn't empty on first load.
setTimeout(() => dispatchAmbientVan(scene), 500);
setTimeout(() => dispatchAmbientVan(scene), 2500);
function sendPresence() {
	if (!liveSocket || liveSocket.readyState !== WebSocket.OPEN) return;
	// In immersive XR the universal camera is detached and the XR camera is
	// active. Read whichever is currently driving the view so head movement in
	// VR also drives the broadcast position.
	const active = scene.activeCamera ?? cam;
	const pos = active.globalPosition ?? active.position;
	const ry = (active as { rotation?: { y: number } }).rotation?.y ?? cam.rotation.y;
	liveSocket.send(JSON.stringify({
		type: "presence",
		pose: { x: pos.x, y: pos.y, z: pos.z, ry },
	}));
}

let t = 0;
engine.runRenderLoop(() => {
	const dt = engine.getDeltaTime() / 1000;
	t += dt;

	ghosts.tick(dt);
	presenceTimer += dt;
	if (presenceTimer >= 0.2) {
		presenceTimer = 0;
		sendPresence();
	}
	ambientTimer += dt;
	if (ambientTimer >= 8 + Math.random() * 8) {
		ambientTimer = 0;
		dispatchAmbientVan(scene);
	}

	npcs.tick(dt);
	weather.tick(dt, scene.activeCamera ?? cam);

	// Ambient stepper hum + fan tracks how many printers are currently running.
	const printingCount = Array.from(printerById.values()).filter((p) => p.status === "printing").length;
	const totalPrinters = Math.max(1, printerById.size);
	sfx.setAmbientLevel(printingCount / totalPrinters);

	// Worker NPCs walk to a failed printer when repairing, idle otherwise.
	const failedPrinterPositions = new Map<string, Vector3>();
	for (const p of printerById.values()) {
		if (p.status === "failed") {
			const mesh = warehouse.printers.get(p.id);
			if (mesh) failedPrinterPositions.set(p.id, mesh.root.position);
		}
	}
	workerVisuals.tick(dt, failedPrinterPositions);
	weatherTimer += dt;
	if (weatherTimer >= 90 + Math.random() * 60) {
		weatherTimer = 0;
		rollWeather();
	}

	// Proximity-based panel auto-open. Find the zone (if any) whose center is
	// within its radius of the camera.
	let zoneId: string | null = null;
	for (const z of interactZones) {
		const dx = cam.position.x - z.center.x;
		const dz = cam.position.z - z.center.z;
		if (dx * dx + dz * dz <= z.radius * z.radius) {
			zoneId = z.id;
			break;
		}
	}
	if (zoneId !== currentZoneId) {
		currentZoneId = zoneId;
		setActivePanel(zoneId ? ZONE_TO_TAB[zoneId]! : null);
	}
	for (const [id, mesh] of warehouse.printers) {
		const p = printerById.get(id);
		if (!p) continue;
		const baseY = 0.12;
		if (p.status === "printing") {
			// Gentle bed bob — slower than the previous 3× so it reads like a real moving bed.
			mesh.bed.position.y = baseY + 0.015 * Math.sin(t * 2);
			(mesh.bed.material as { emissiveColor?: { set: (r: number, g: number, b: number) => void } }).emissiveColor?.set(0, 0, 0);

			// Hotend traces a Lissajous-like path across the bed.
			// Gantry slides on X (range ±0.2 from center).
			mesh.gantry.position.x = 0.2 * Math.sin(t * 3.7 + id.length);
			// Hotend slides on Z relative to gantry (range ±0.2).
			mesh.hotend.position.z = 0.2 * Math.cos(t * 5.1 + id.length * 2);

			// Filament spool spins around its own local axis (NOT world Y, which
			// would orbit it instead of spin in place).
			mesh.spool.rotate(Vector3.Up(), dt * 4, Space.LOCAL);
			// Spool diameter shrinks over the print — animate via scale on the cylinder's
			// X/Z (since it's rotated 90° around Z, diameter is X/Z scale).
			const activeContract = Array.from(contractById.values()).find(
				(c) => c.assignedPrinterId === id && c.status === "in-progress",
			);
			if (activeContract) {
				const consumed = (activeContract.progress ?? 0) / Math.max(1, activeContract.quantity);
				const targetScale = 1 - 0.4 * consumed;
				mesh.spool.scaling.x += (targetScale - mesh.spool.scaling.x) * Math.min(1, dt * 2);
				mesh.spool.scaling.z = mesh.spool.scaling.x;
			}
		} else if (p.status === "failed") {
			mesh.bed.position.y = baseY;
			const flash = 0.5 + 0.5 * Math.sin(t * 6);
			(mesh.bed.material as { emissiveColor?: { set: (r: number, g: number, b: number) => void } }).emissiveColor?.set(flash * 0.6, 0, 0);
			// Park the gantry/hotend center and stop the spool when failed.
			mesh.gantry.position.x += (0 - mesh.gantry.position.x) * Math.min(1, dt * 4);
			mesh.hotend.position.z += (0 - mesh.hotend.position.z) * Math.min(1, dt * 4);
		} else {
			mesh.bed.position.y = baseY;
			(mesh.bed.material as { emissiveColor?: { set: (r: number, g: number, b: number) => void } }).emissiveColor?.set(0, 0, 0);
			// Idle: park gantry/hotend at home position.
			mesh.gantry.position.x += (0 - mesh.gantry.position.x) * Math.min(1, dt * 3);
			mesh.hotend.position.z += (0 - mesh.hotend.position.z) * Math.min(1, dt * 3);
			// Reset spool size when idle.
			mesh.spool.scaling.x += (1 - mesh.spool.scaling.x) * Math.min(1, dt * 2);
			mesh.spool.scaling.z = mesh.spool.scaling.x;
		}

		// Sync the 3D label.
		mesh.label.title.text = p.displayName;
		if (p.status === "failed" && p.failureMode) {
			mesh.label.status.text = p.failureMode.toUpperCase();
			mesh.label.status.color = "#f88";
			mesh.label.root.color = "#f88";
		} else if (p.status === "printing") {
			mesh.label.status.text = "PRINTING";
			mesh.label.status.color = "#fd6";
			mesh.label.root.color = "#fd6";
		} else {
			mesh.label.status.text = "IDLE";
			mesh.label.status.color = "#9c9";
			mesh.label.root.color = "#7df";
		}

		// If the printer is failed and we don't have the part to repair it,
		// tell the player explicitly on the progress line.
		const REPAIR_PART_FOR: Record<string, { sku: string; label: string }> = {
			"nozzle-clog": { sku: "nzl-brass-04", label: "Brass nozzle" },
			"thermal-runaway": { sku: "thermistor-pt100", label: "PT100 thermistor" },
			"support-collapse": { sku: "build-plate-pei", label: "PEI build plate" },
			"warping": { sku: "build-plate-pei", label: "PEI build plate" },
			"resin-contamination": { sku: "filament-pla-1kg", label: "PLA filament" },
			"power-failure": { sku: "thermistor-pt100", label: "PT100 thermistor" },
		};
		if (p.status === "failed" && p.failureMode && REPAIR_PART_FOR[p.failureMode]) {
			const req = REPAIR_PART_FOR[p.failureMode]!;
			const have = player?.inventory[req.sku] ?? 0;
			if (have <= 0) {
				mesh.label.progress.text = `⚠️ Out of ${req.label} — buy at shop`;
				mesh.label.progress.color = "#f88";
			}
		} else {
			mesh.label.progress.color = "#fd6";
		}
		const activeContract = Array.from(contractById.values()).find(
			(c) => c.assignedPrinterId === id && c.status === "in-progress",
		);
		if (activeContract) {
			const remaining = activeContract.quantity - (activeContract.progress ?? 0);
			// Each game-tick (~3 sec real time) makes 1 unit. ETA in real seconds.
			const etaSec = Math.max(0, remaining * 3);
			const etaStr = etaSec < 60 ? `${etaSec}s` : `${Math.round(etaSec / 60)}m`;
			mesh.label.progress.text = `${activeContract.progress ?? 0}/${activeContract.quantity} · ETA ${etaStr} · ${activeContract.clientName}`;
		} else {
			mesh.label.progress.text = "";
		}

		// Grow a print object on the bed as the contract progresses.
		if (activeContract && p.status === "printing") {
			const ratio = Math.min(1, (activeContract.progress ?? 0) / Math.max(1, activeContract.quantity));
			// Smoothly chase the target so it grows over the print, not in steps.
			const target = 0.02 + ratio * 0.4;
			mesh.print.isVisible = true;
			mesh.print.scaling.y += (target - mesh.print.scaling.y) * Math.min(1, dt * 2);
		} else if (p.status === "failed" && activeContract) {
			// Leave whatever was printed there but stop growing.
			mesh.print.isVisible = true;
		} else {
			mesh.print.scaling.y += (0.001 - mesh.print.scaling.y) * Math.min(1, dt * 4);
			if (mesh.print.scaling.y < 0.01) mesh.print.isVisible = false;
		}
	}
	scene.render();
});
window.addEventListener("resize", () => engine.resize());
window.addEventListener("orientationchange", () => engine.resize());
// Some browsers don't reliably fire resize on dvh changes (mobile address bar);
// a ResizeObserver on the canvas catches every layout shift.
new ResizeObserver(() => engine.resize()).observe(canvas);
// Initial nudge after first frame to make sure Babylon's internal buffer matches CSS.
requestAnimationFrame(() => engine.resize());
